import { useEffect, useMemo, useRef, useState } from 'react';
import { Heading, HEADING_TYPES, DatePicker, ChoiceChips } from 'cdk-radial';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment, { Moment } from 'moment';

export const DatePresetChipGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const StyledDatePickerWrapper = styled.div<{ maxWidth?: string }>`
  max-width: ${(props) => props.maxWidth || '250px'};
  width: 100%;
  position: relative;
  .DayPicker {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 8px;
    z-index: 1000;
  }
`;

export const UserCreationFilter = ({ filter, onChange, onValidationError }: any) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [datePreset, setDatePreset] = useState<string>('custom');
  const isDateModeInitialized = useRef(false);

  // Block future dates from tomorrow onwards
  const blockedFutureDates = useMemo(
    () => [
      {
        startDate: moment().add(1, 'day'),
        endDate: moment('2100-12-31'),
      },
    ],
    []
  );

  // Validate dates: require either both start/end dates OR a preset (not custom)
  useEffect(() => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      onValidationError(true);
    } else if (startDate && moment(startDate).isAfter(moment(), 'day')) {
      onValidationError(true);
    } else if (endDate && moment(endDate).isAfter(moment(), 'day')) {
      onValidationError(true);
    } else if (startDate && endDate && moment(startDate).isAfter(endDate, 'day')) {
      // Start date must be on or before the end date
      onValidationError(true);
    } else if (!startDate && !endDate && datePreset === 'custom') {
      onValidationError(true);
    } else {
      onValidationError(false);
    }
  }, [startDate, endDate, datePreset, onValidationError]);

  // Initialize from the saved filter (only once)
  useEffect(() => {
    if (isDateModeInitialized.current) return;

    const defaultTo7Days = () => {
      // Make the default selection part of the filter state so saving without
      // touching anything persists what the UI shows.
      setDatePreset('7days');
      onChange({
        startDate: null,
        endDate: null,
        dateRangePreset: 6,
      });
    };

    if (filter?.startDate && filter?.endDate) {
      setStartDate(moment(filter.startDate));
      setEndDate(moment(filter.endDate));
      setDatePreset('custom');
    } else if (Number(filter?.dateRangePreset) > 0) {
      const savedPreset = Number(filter.dateRangePreset);
      if (savedPreset === 6) {
        setDatePreset('7days');
      } else if (savedPreset === 13) {
        setDatePreset('14days');
      } else if (savedPreset === 30) {
        setDatePreset('lastmonth');
      } else {
        // Unknown preset value: fall back to the default instead of leaving the user stuck
        defaultTo7Days();
      }
    } else {
      defaultTo7Days();
    }
    isDateModeInitialized.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleStartDateChange = (date: Moment | null) => {
    setStartDate(date);
    setDatePreset('custom');

    onChange({
      startDate: date ? date.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      dateRangePreset: 0,
    });
  };

  const handleEndDateChange = (date: Moment | null) => {
    setEndDate(date);
    setDatePreset('custom');

    onChange({
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: date ? date.format('YYYY-MM-DD') : null,
      dateRangePreset: 0,
    });
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    setStartDate(null);
    setEndDate(null);

    let daysAgo;
    switch (preset) {
      case '7days':
        daysAgo = 6;
        break;
      case '14days':
        daysAgo = 13;
        break;
      case 'lastmonth':
        daysAgo = 30;
        break;
      default:
        return;
    }

    onChange({
      startDate: null,
      endDate: null,
      dateRangePreset: daysAgo,
    });
  };

  return (
    <div>
      <Heading level={3} type={HEADING_TYPES.SUBTITLE_3}>
        {t('dashboard.dateSettings', 'Date Settings')}
      </Heading>

      <DatePresetChipGroup>
        <ChoiceChips
          dataTestId="user-creation-date-preset-chips"
          onClick={(value: string) => handleDatePreset(value)}
          id="user-creation-date-preset-chips"
          name="datePreset"
          options={[
            { label: t('dashboard.datePresets.last7Days') as string, value: '7days' },
            { label: t('dashboard.datePresets.last14Days') as string, value: '14days' },
            { label: t('dashboard.datePresets.lastMonth') as string, value: 'lastmonth' },
          ]}
          value={datePreset === 'custom' ? '' : datePreset}
        />
      </DatePresetChipGroup>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
        <StyledDatePickerWrapper maxWidth="250px">
          <DatePicker
            dataTestId="user-creation-filter-start-date-picker"
            date={startDate}
            dateFormat="MM/DD/YYYY"
            dateInputProps={{
              enableCustomValidation: false,
              errorMessage: '',
              hasError: false,
              isReadOnly: false,
              isRequired: false,
              isDisabled: false,
              size: 'standard',
              onError: () => {},
            }}
            allowDaysInPast={true}
            blockedDateRanges={blockedFutureDates}
            id="user-creation-filter-start-date-picker"
            label={t('dashboard.startDate', 'Start Date') as string}
            maximumYear={2100}
            minimumYear={1900}
            name="user-creation-filter-start-date-picker"
            numberOfMonths={1}
            onDateChange={handleStartDateChange}
            shortcutChipsPosition="none"
          />
        </StyledDatePickerWrapper>

        <StyledDatePickerWrapper maxWidth="250px">
          <DatePicker
            dataTestId="user-creation-filter-end-date-picker"
            date={endDate}
            dateFormat="MM/DD/YYYY"
            dateInputProps={{
              enableCustomValidation: false,
              errorMessage: '',
              hasError: false,
              isReadOnly: false,
              isRequired: false,
              isDisabled: false,
              size: 'standard',
              onError: () => {},
            }}
            allowDaysInPast={true}
            blockedDateRanges={blockedFutureDates}
            id="user-creation-filter-end-date-picker"
            label={t('dashboard.endDate', 'End Date') as string}
            maximumYear={2100}
            minimumYear={1900}
            name="user-creation-filter-end-date-picker"
            numberOfMonths={1}
            onDateChange={handleEndDateChange}
            shortcutChipsPosition="none"
          />
        </StyledDatePickerWrapper>
      </div>
    </div>
  );
};

export default UserCreationFilter;
